import { api } from '../client';
import type { ClientsResponse, TimelineResponse, MetricsHistoryPoint, Client } from '../../types/client';

export interface SshSession {
  session_id: string;
  client_id: string;
  port: number;
  status: 'pending' | 'active' | 'expired' | 'revoked';
  expires_at: string;
  created_at: string;
}

export interface TenantSearchResult {
  _id?: string;
  id?: string;
  tenant_id?: string;
  name?: string;
  tenant_name?: string;
  subdomain?: string;
}

export interface Deployment {
  _id?: string;
  id?: string;
  deployment_id?: string;
  name?: string;
  deployment_name?: string;
}

export interface ClientsParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  deployment?: string;
  sort?: string;
  order?: string;
}

export const clientsApi = {
  list: (params: ClientsParams = {}) =>
    api.get<ClientsResponse>('/api/clients', params as Record<string, string | number>),

  get: (clientId: string) =>
    api.get<{ success: boolean; client: Client }>(`/api/clients/${clientId}`),

  delete: (clientId: string) =>
    api.delete<{ success: boolean }>(`/api/clients/${clientId}`),

  timeline: (clientId: string, hours = 24) =>
    api.get<TimelineResponse>(`/api/clients/${clientId}/timeline`, { hours }),

  metrics: async (clientId: string, range?: string) => {
    const res = await api.get<{ success: boolean; history: Array<{ cpu_percent: number; memory_percent: number; disk_percent: number; latency_ms?: number; collected_at: string }> }>(
      `/api/ports/${clientId}/metrics/history`, { range: range || undefined }
    );
    return {
      success: res.success,
      metrics: (res.history || []).map(h => ({
        timestamp: h.collected_at,
        cpu_percent: h.cpu_percent,
        memory_percent: h.memory_percent,
        disk_percent: h.disk_percent,
        latency_ms: h.latency_ms,
      })),
    };
  },

  offlineEvents: (clientId: string, page = 1, limit = 10) =>
    api.get<{ success: boolean; events: Array<{ went_offline: string; came_online: string | null; duration_ms: number }>; total: number; page: number; pages: number }>(
      `/api/clients/${clientId}/offline-events`, { page, limit }
    ),

  updateNotes: (clientId: string, notes: string) =>
    api.put<{ success: boolean }>(`/api/clients/${clientId}/notes`, { notes }),

  rename: (clientId: string, client_name: string) =>
    api.put<{ success: boolean }>(`/api/clients/${clientId}/name`, { client_name }),

  updateTags: (clientId: string, tags: string[]) =>
    api.put<{ success: boolean }>(`/api/clients/${clientId}/tags`, { tags }),

  updateDeployment: (clientId: string, deployment_id: string, deployment_name: string) =>
    api.put<{ success: boolean }>(`/api/clients/${clientId}/deployment`, { deployment_id, deployment_name }),

  // SSH Sessions
  sshSessions: (clientId: string) =>
    api.get<{ success: boolean; sessions: SshSession[] }>(`/api/ssh-sessions/client/${clientId}`),

  createSshSession: (clientId: string, durationMinutes = 60) =>
    api.post<{ success: boolean; session?: SshSession; error?: string }>('/api/ssh-sessions', { client_id: clientId, duration_minutes: durationMinutes }),

  revokeSshSession: (sessionId: string) =>
    api.delete<{ success: boolean; error?: string }>(`/api/ssh-sessions/${sessionId}`),

  // Chisel tunnel (TRIAL)
  toggleChisel: (clientId: string, enabled: boolean, durationMinutes = 30) =>
    api.put<{ success: boolean; chisel_enabled: boolean; chisel_expires_at: string }>(`/api/clients/${clientId}/chisel`, { enabled, duration_minutes: durationMinutes }),

  chiselStatus: (clientId: string) =>
    api.get<{ success: boolean; chisel_enabled: boolean; chisel_expires_at: string | null; port: number; tunnel_active: boolean }>(`/api/clients/${clientId}/chisel-status`),

  // Integration linking
  updateIntegration: (clientId: string, integrationId: string | null) =>
    api.put<{ success: boolean; error?: string }>(`/api/clients/${clientId}/integration`, { integration_id: integrationId }),

  // Posist tenant search
  searchTenants: (query: string) =>
    api.get<{ success: boolean; tenants: TenantSearchResult[] }>('/api/posist/tenants/search', { q: query }),

  // Posist deployments for tenant
  getDeployments: (tenantId: string) =>
    api.post<{ success: boolean; deployments: Deployment[] }>('/api/posist/deployments', { tenant_id: tenantId }),

  // Full deployment mapping update (matching EJS)
  updateDeploymentFull: (clientId: string, data: { tenant_id: string | null; tenant_name: string | null; deployment_id: string | null; deployment_name: string | null; subdomain: string | null }) =>
    api.put<{ success: boolean; error?: string }>(`/api/clients/${clientId}/deployment`, data),
};
